import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

export const transactionsApi = createApi({
  reducerPath: "transactionsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { contentTypeJson: true }),
  }),
  tagTypes: ["Transactions", "Transaction", "Sales", "Returns"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,

  endpoints: (builder) => ({
    fetchSalesByDate: builder.query({
      query: (date) => ({ url: "transactions/sales", params: { date } }),
      transformResponse: (res) => res?.data?.data ?? res?.data ?? res ?? [],
      providesTags: [{ type: "Sales", id: "LIST" }],
    }),

    fetchTransactionsByDate: builder.query({
      query: (date) => ({ url: "transactions", params: { date } }),
      transformResponse: (res) => res?.data?.data ?? res?.data ?? res ?? [],
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map((t) => ({ type: "Transaction", id: t.id })),
              { type: "Transactions", id: "LIST" },
            ]
          : [{ type: "Transactions", id: "LIST" }],
    }),

    fetchReturnsByDate: builder.query({
      query: (date) => ({
        url: "transactions",
        params: { date, type: "return" },
      }),
      transformResponse: (res) => res?.data?.data ?? res?.data ?? res ?? [],
      providesTags: [{ type: "Returns", id: "LIST" }],
    }),

    fetchTransactionTypes: builder.query({
      async queryFn() {
        return {
          data: [
            { id: "Taxes", name: "Taxes", price: 60 },
            { id: "Energy", name: "Energy", price: false },
            { id: "Administration", name: "Administration", price: false },
          ],
        };
      },
      providesTags: [{ type: "Transactions", id: "TYPES" }],
    }),

    // ===== Create/Update/Delete
    addTransaction: builder.mutation({
      query: (data) => ({ url: "transactions", method: "POST", body: data }),
      invalidatesTags: [
        { type: "Transactions", id: "LIST" },
        { type: "Sales", id: "LIST" },
      ],
    }),
    transactionUpdate: builder.mutation({
      query: (data) => ({ url: "transactions", method: "PUT", body: data }),
      invalidatesTags: (res, _err, arg) => [
        { type: "Transactions", id: "LIST" },
        ...(arg?.id ? [{ type: "Transaction", id: arg.id }] : []),
      ],
    }),
    deleteTransaction: builder.mutation({
      query: (id) => ({ url: `transactions/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "Transactions", id: "LIST" }],
    }),

    recordReturn: builder.mutation({
      query: (payload) => ({
        url: "transactions/returns",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        { type: "Returns", id: "LIST" },
        { type: "Transactions", id: "LIST" },
        { type: "Sales", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useFetchSalesByDateQuery,
  useLazyFetchSalesByDateQuery,
  useFetchTransactionsByDateQuery,
  useLazyFetchTransactionsByDateQuery,
  useFetchReturnsByDateQuery,
  useFetchTransactionTypesQuery,
  useAddTransactionMutation,
  useTransactionUpdateMutation,
  useDeleteTransactionMutation,
  useRecordReturnMutation,
} = transactionsApi;
